import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import StudentNav from "../Navbars/StudentNav";
import AdminNav from "../Navbars/AdminNav";
import TeacherNav from "../Navbars/TeacherNav";
import GuestNav from "../Navbars/GuestNav";
import { UserContext } from "../UserContext";
import Header from "../Header";
import Footer from "../Footer";
import { AiOutlineCloudUpload } from "react-icons/ai";
import "./ActivitiesAndProjects.css";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom/dist/umd/react-router-dom.development";

import puzzleicon from "./ActivityIcons/AP_GameIcon.png";
import videoicon from "./ActivityIcons/AP_VideoIconNew.png";
import brainicon from "./ActivityIcons/AP_BrainIconNew.png";
import SpeedQuiz from "./ActivityIcons/SpeedQuizIconNew.png";
import oralicon from "./ActivityIcons/AP_OralIconNew.png";
import writtenicon from "./ActivityIcons/AP_WrittenIconNew.png";

import VideoActImg from "./ActivityIcons/VideoActImg.png";
import GameActImg from "./ActivityIcons/GameActImg.png";
import MentalActImg from "./ActivityIcons/MentalActImg.png";
import OralActImg from "./ActivityIcons/OralActImg.png";
import WrittenActImg from "./ActivityIcons/WrittenActImg.png";
import MCQActImg from "./ActivityIcons/MCQActImg.png";
import SpeedQuizImg from "./ActivityIcons/SpeedQuizImg.png";

import axiosInstance from "../ManageContent/managecontentforms/axiosInstance";
import { useNavigate } from "react-router-dom";
import ModuleProjects from "./ModuleProjects";
import LessonProjects from "./LessonProjects";
import { toast } from "react-toastify";
import ActivityList from "./ActivityList";

const isDataEqual = (oldData, newData) => {
  // Compare projects
  const projectsEqual =
    JSON.stringify(oldData.projects) === JSON.stringify(newData.projects);
  // Compare lesson projects
  const lessonProjectsEqual =
    JSON.stringify(oldData.lessonProjects) ===
    JSON.stringify(newData.lessonProjects);
  // Compare activities
  const activitiesEqual =
    JSON.stringify(oldData.activities) === JSON.stringify(newData.activities);

  return projectsEqual && lessonProjectsEqual && activitiesEqual;
};

const ActivitiesAndProjects = ({ onSearch, searchValue }) => {
  const [activityId, setActivityId] = useState(null);
  const [courseID, setCourseID] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [lessonProjects, setLessonProjects] = useState([]);
  const [activities, setActivities] = useState({});
  const [profileData, setProfileData] = useState({});
  const [refresh, setRefresh] = useState(false);
  const [polling, setPolling] = useState(null);
  const [search, setSearch] = useState(searchValue || "");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { courseId } = useParams();
  const navigate = useNavigate();

  const {
    isAdmin,
    isTeacher,
    isStudent,
    isGuest,
    setUserRole,
    setUserInfo,
    userInfo,
  } = useContext(UserContext);

  const fetchProfileData = useCallback(
    async (studentId) => {
      try {
        const response = await axiosInstance.get(
          `/getLessonActivityProfileData/${studentId}/${courseId}`,
          { withCredentials: true }
        );
        setProfileData(response.data?.data || {});
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    },
    [courseId]
  );

  const fetchUserData = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/", { withCredentials: true });
      const user = response.data;
      setUserInfo(user);

      const studentResponse = await axiosInstance.post(
        "/getStudentIdByUserAndCourse",
        {
          userId: user.User_id,
          courseId: courseId
        }
      );

      let studentId = null;
      if (studentResponse.data.success) {
        studentId = studentResponse.data.Student_id;
      } else {
        // Fallback to old method if specific not found (though unlikely if course is valid)
        // or handle error. For now, try old method as backup or just log
        console.warn("Specific student ID not found, trying generic lookup...");
        const genericResponse = await axiosInstance.post("/getStudentIDfromUser", { UserId: user.User_id });
        studentId = genericResponse.data[0]?.Student_id || null;
      }

      setStudentId(studentId);

      if (studentId) {
        await fetchProfileData(studentId);

        const activityResponse = await axiosInstance.post(
          "/getActivityIDfromStudent",
          { StudentId: studentId }
        );
        const activityId = activityResponse.data[0]?.Activity_id || null;
        setActivityId(activityId);

        if (activityId) {
          const courseResponse = await axiosInstance.post(
            "/getCourseIDfromActivityID",
            { ActivityID: activityId }
          );
          setCourseID(courseResponse.data[0]?.Course_id);
        }

        const [projectsData, lessonProjectsData, activityData] =
          await Promise.all([
            axiosInstance.post("/getStudentModuleProjectAssignment", {
              studentId,
              CourseID: courseId,
            }),
            axiosInstance.post("/getStudentLessonProjectAssignment", {
              studentId,
              CourseID: courseId,
            }),
            axiosInstance.post("/getStudentLessonActivity", {
              studentId,
              CourseID: courseId,
            }),
          ]);

        setProjects(projectsData.data?.data || []);
        setLessonProjects(lessonProjectsData.data || []);
        organizeActivities(activityData.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [courseId, fetchProfileData]);

  const organizeActivities = (data) => {
    const organized = {
      Video: [],
      Game: [],
      Mental: [],
      Oral: [],
      Written: [],
      "Speed Quiz": [],
      MCQ: [],
    };
    data.forEach((activity) => {
      if (organized[activity.ActivityType]) {
        organized[activity.ActivityType].push({ activity });
      }
    });
    setActivities(organized);
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/logout", {}, { withCredentials: true });
      setUserInfo(null);
      setUserRole("guest");
      navigate("/");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const fetchLatestData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [projectsData, lessonProjectsData, activityData] =
        await Promise.all([
          axiosInstance.post("/getStudentModuleProjectAssignment", {
            studentId,
            CourseID: courseId,
          }),
          axiosInstance.post("/getStudentLessonProjectAssignment", {
            studentId,
            CourseID: courseId,
          }),
          axiosInstance.post("/getStudentLessonActivity", {
            studentId,
            CourseID: courseId,
          }),
        ]);

      // Create new data object
      const newData = {
        projects: projectsData.data?.data || [],
        lessonProjects: lessonProjectsData.data || [],
        activities: activityData.data?.data || [],
      };

      // Create current data object
      const currentData = {
        projects,
        lessonProjects,
        activities: Object.values(activities)
          .flat()
          .map((item) => item.activity),
      };

      // Only update if data has changed
      if (!isDataEqual(currentData, newData)) {
        setProjects(newData.projects);
        setLessonProjects(newData.lessonProjects);
        organizeActivities(newData.activities);
      }
    } catch (error) {
      console.error("Error fetching latest data:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [studentId, courseId, projects, lessonProjects, activities]);

  useEffect(() => {
    // Only set up polling if we have required data
    if (!studentId || !courseId) {
      return;
    }

    const handleMessage = (event) => {
      if (event.data === "activityCompleted") {
        fetchLatestData();
      }
    };

    window.addEventListener("message", handleMessage);

    // Clear any existing interval before setting a new one
    if (polling) {
      clearInterval(polling);
    }

    // Set up new polling interval
    const newPollInterval = setInterval(fetchLatestData, 15000);
    setPolling(newPollInterval);

    // Cleanup function
    return () => {
      window.removeEventListener("message", handleMessage);
      if (newPollInterval) {
        clearInterval(newPollInterval);
      }
    };
  }, [studentId, courseId]); // Only re-run when studentId or courseId changes

  useLayoutEffect(() => {
    fetchUserData();
  }, [fetchUserData, refresh]);

  const renderNavBar = () => {
    if (isAdmin)
      return (
        <AdminNav
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          onLogout={handleLogout}
        />
      );
    if (isTeacher)
      return (
        <TeacherNav
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          onLogout={handleLogout}
        />
      );
    if (isStudent)
      return (
        <StudentNav
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          onLogout={handleLogout}
        />
      );
    if (isGuest) return <GuestNav onLogin={() => setUserRole("guest")} />;
    return null;
  };

  const handleImageError = (e) => {
    const currentSrc = e.target.src;
    const avatar = userInfo.avatar;

    if (avatar && !currentSrc.includes("placeholder")) {
      // Extract filename from avatar URL (handle S3 URLs properly)
      let filename = avatar.split("/").pop();

      // If it's an S3 URL, decode the filename
      if (filename.includes("%20")) {
        filename = decodeURIComponent(filename);
      }

      // Try different URL patterns - prioritize S3 since that's where images are stored
      const rawAvatar = avatar.replace("//", "");
      const alternatives = [
        `https://${rawAvatar}`, // Direct S3 URL with https
        `https://s3.amazonaws.com/${rawAvatar.replace(
          "s3.amazonaws.com/",
          ""
        )}`, // Cleaned S3 URL
        // Skip local backend URLs since we know they don't exist
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIiBzdHJva2U9IiNjY2MiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+VXNlcjwvdGV4dD48L3N2Zz4=", // User icon placeholder
      ];

      // Find the next alternative that hasn't been tried
      const currentIndex = alternatives.findIndex((alt) => alt === currentSrc);
      const nextIndex = currentIndex + 1;

      if (nextIndex < alternatives.length) {
        e.target.src = alternatives[nextIndex];
      } else {
        e.target.src = "https://via.placeholder.com/150?text=No+Image";
      }
    }
  };

  return (
    <>
      {/* {renderNavBar()} */}
      {userInfo.usertype == "Student" ? (
        <StudentNav
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          onLogout={handleLogout}
        />
      ) : userInfo.usertype == "Teacher" ? (
        <TeacherNav
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          onLogout={handleLogout}
        />
      ) : userInfo.usertype == "Admin" ? (
        <AdminNav
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          onLogout={handleLogout}
        />
      ) : (
        <GuestNav onLogin={() => setUserRole("guest")} />
      )}
      <div className="AandP-page">
        <Header title="Activities and Projects" />
        <h2 className="course--title">{`${profileData?.coursename}`}</h2>
        <div className="AP_container">
          {/* <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}> */}
          {projects.length > 0 && (
            <ModuleProjects
              title="Module Project"
              projects={projects}
              textColor="#5665e9"
              backgroundColor="#dae8ff"
              iconBackground="#aec7f0"
              image={puzzleicon}
              activityID={activityId}
              courseID={courseID}
              uploadApiRoute="/api/activitiesandprojects/uploadStudentModuleProject"
              studentid={studentId}
            />
          )}
          {lessonProjects.length > 0 && (
            <LessonProjects
              title="Lesson Project"
              projects={lessonProjects}
              textColor="#5665e9"
              backgroundColor="#dae8ff"
              iconBackground="#aec7f0"
              image={puzzleicon}
              activityID={activityId}
              courseID={courseID}
              studentid={studentId}
              uploadApiRoute="/api/activitiesandprojects/uploadStudentLessonProject"
            />
          )}
          {/* </div> */}
        </div>

        {Object.entries(activities).map(([title, projectList]) =>
          projectList.length > 0 ? (
            <div className="AP_container" key={title}>
              <ActivityList
                title={title}
                projects={projectList}
                textColor={getTextColor(title)}
                backgroundColor={getBackgroundColor(title)}
                iconBackground={getIconBackground(title)}
                image={getImageForTitle(title)}
                image1={getSecondImageForTitle(title)}
                activityID={activityId}
                courseID={courseID}
                studentId={studentId}
                setRefresh={setRefresh}
                uploadApiRoute="/api/activitiesandprojects/uploadStudentGameActivity"
                profileData={profileData}
              />
            </div>
          ) : null
        )}
      </div>
    </>
  );
};

const getTextColor = (title) => {
  const colors = {
    Video: "#5A5252",
    Game: "#5A5252",
    Mental: "#5A5252",
    Oral: "#5A5252",
    Written: "#5A5252",
    "Speed Quiz": "#5A5252",
    MCQ: "#5A5252",
  };
  return colors[title] || "#000";
};

const getBackgroundColor = (title) => {
  const backgrounds = {
    Video: "#DEFFC3",
    Game: "#E5F7FE",
    Mental: "#FFC980B0",
    Oral: "#FFD2F4",
    Written: "#EDC4FF",
    "Speed Quiz": "#FEFDA0",
    MCQ: "#FEBFBD",
  };
  return backgrounds[title] || "#fff";
};

const getIconBackground = (title) => {
  const iconBackgrounds = {
    Video: "rgb(205, 243, 245)",
    Game: "#f2fcae",
    Mental: "#fae7b4",
    Oral: "#fccada",
    Written: "#d0c2fc",
    "Speed Quiz": "rgb(200, 248, 228)",
    MCQ: "#d0c2fc",
  };
  return iconBackgrounds[title] || "#ccc";
};

const getImageForTitle = (title) => {
  const images = {
    Video: videoicon,
    Game: puzzleicon,
    Mental: brainicon,
    Oral: oralicon,
    Written: writtenicon,
    "Speed Quiz": SpeedQuiz,
    MCQ: brainicon,
  };
  return images[title] || puzzleicon;
};

const getSecondImageForTitle = (title) => {
  const images = {
    Video: VideoActImg,
    Game: GameActImg,
    Mental: MentalActImg,
    Oral: OralActImg,
    Written: WrittenActImg,
    "Speed Quiz": SpeedQuizImg,
    MCQ: MCQActImg,
  };
  return images[title] || puzzleicon;
};


export default ActivitiesAndProjects;
